/**
 * HostelDashboard.jsx — CAMP-RES-DEMO-01
 * Executive occupancy dashboard for Kaveri Bhavan, Vaagdevi College of Engineering.
 * Module 08.02 — Residence, Transport & Campus Services
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Building2, BedDouble, Users, CheckCircle2, Clock, AlertCircle,
  CalendarCheck, TrendingUp, ChevronRight, PlusCircle, Star, Home,
  BookOpen, RefreshCw, RotateCcw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  MetricCard, MetricCardGrid, PremiumHeroBanner, DashboardPanel,
  BookingsSummaryPanel, QuickActionGrid, RecentBookingsList,
} from "../components/hostel/HostelMetrics";

const API = process.env.REACT_APP_BACKEND_URL;
const VCE_INST = "44444444-4444-4444-4444-444444444444";

// Dark-background badge colours — used in GL Reconciliation tab and booking list/detail pages
const STATUS_CONFIG = {
  DRAFT:       { label: "Draft",       color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  RESERVED:    { label: "Reserved",    color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  CONFIRMED:   { label: "Confirmed",   color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  CHECKED_IN:  { label: "Checked In",  color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  CHECKED_OUT: { label: "Checked Out", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  CANCELLED:   { label: "Cancelled",   color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

// Light-background badge colours — used inside the white Recent Bookings panel
const LIGHT_STATUS_CONFIG = {
  DRAFT:       { label: "Draft",       color: "bg-slate-100 text-slate-600 border-slate-200" },
  RESERVED:    { label: "Reserved",    color: "bg-blue-50 text-blue-700 border-blue-200" },
  CONFIRMED:   { label: "Confirmed",   color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  CHECKED_IN:  { label: "Checked In",  color: "bg-amber-50 text-amber-700 border-amber-200" },
  CHECKED_OUT: { label: "Checked Out", color: "bg-purple-50 text-purple-700 border-purple-200" },
  CANCELLED:   { label: "Cancelled",   color: "bg-red-50 text-red-700 border-red-200" },
};

export default function HostelDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [reconData, setReconData] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState(null);
  const [initiatingGL, setInitiatingGL] = useState(null);

  const institutionId = user?.institution_id || VCE_INST;

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/api/v1/hostel/dashboard?institution_id=${institutionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [token, institutionId]);

  const loadRecon = () => {
    if (!token) return;
    setReconLoading(true);
    axios
      .get(`${API}/api/v1/hostel/bookings/reconciliation-report`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { setReconData(r.data); setReconLoading(false); setReconError(null); })
      .catch((e) => {
        const msg = e.response?.data?.detail || e.message;
        setReconError(msg);
        setReconLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === "reconciliation" && !reconData) loadRecon();
  }, [activeTab]);

  const handleInitiateGL = async (bookingId) => {
    setInitiatingGL(bookingId);
    try {
      const r = await axios.post(
        `${API}/api/v1/hostel/bookings/${bookingId}/gl-recovery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = r.data;
      toast.success(
        d.idempotent
          ? `Journal already exists (${d.gl_journal_status}). No duplicate created.`
          : `DRAFT journal created. Advance via GL Journals workspace.`
      );
      loadRecon();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || e.message);
      toast.error(msg || "GL initiation failed");
    } finally {
      setInitiatingGL(null);
    }
  };

  const CAN_GL_VALIDATE = ["finance_manager", "cfo", "institution_admin", "super_admin"].includes(user?.role || "");

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="hostel-dashboard-loading">
      <div className="flex gap-2 items-center text-slate-400">
        <Building2 className="w-5 h-5 animate-pulse" /> Loading Kaveri Bhavan…
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 text-red-400" data-testid="hostel-dashboard-error">
      Unable to load dashboard: {error}
    </div>
  );

  const rs = data?.room_stats || {};
  const bs = data?.booking_stats || {};

  // Stage D — Room Occupancy Rate (AOS-UX-KPI-01)
  // Denominator: eligible rooms = total_rooms - MAINTENANCE - OUT_OF_SERVICE
  // Numerator:   rooms with status PARTIALLY_OCCUPIED or FULLY_OCCUPIED
  // This avoids the previous error of mixing bed counts with room counts.
  const eligibleRooms = Math.max(
    (rs.total_rooms || 0) - (rs.maintenance || 0) - (rs.out_of_service || 0),
    0,
  );
  const roomOccPct = eligibleRooms > 0
    ? Math.round(((rs.occupied || 0) / eligibleRooms) * 100)
    : 0;

  return (
    <div className="p-6 space-y-5" data-testid="hostel-dashboard">
      {/* Premium hero header */}
      <PremiumHeroBanner
        title="Kaveri Bhavan"
        badgeLabel="Premium Residential"
        badgeVariant="amber"
        subtitle="Vaagdevi College of Engineering · Executive Occupancy Dashboard"
        demoNotice="[DEMO] Illustrative data — synthetic accommodation imagery and rates"
        icon={<Building2 className="w-7 h-7 text-amber-400" />}
      >
        <Button
          data-testid="btn-new-booking"
          onClick={() => navigate("/campus/hostel/bookings/new")}
          className="bg-amber-600 hover:bg-amber-500 text-white shadow-md"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> New Booking
        </Button>
      </PremiumHeroBanner>

      {/* Tab Bar */}
      <div className="flex gap-1 border-b border-slate-700/60 bg-slate-800/40 rounded-t-lg px-1" data-testid="dashboard-tabs">
        {[
          { id: "overview", label: "Overview", icon: Building2 },
          { id: "reconciliation", label: "GL Reconciliation", icon: BookOpen },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px
              ${activeTab === id
                ? "border-amber-500 text-amber-400"
                : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <OverviewContent
          rs={rs} bs={bs} roomOccPct={roomOccPct} eligibleRooms={eligibleRooms}
          bookings={data?.recent_bookings || []}
          navigate={navigate}
        />
      )}

      {/* GL Reconciliation Tab */}
      {activeTab === "reconciliation" && (
        <GLReconciliationTab
          reconData={reconData}
          reconLoading={reconLoading}
          reconError={reconError}
          onRefresh={loadRecon}
          onInitiateGL={handleInitiateGL}
          initiatingGL={initiatingGL}
          canGLValidate={CAN_GL_VALIDATE}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewContent({ rs, bs, roomOccPct, eligibleRooms, bookings, navigate }) {
  // Offline room count for inventory transparency (Stage D)
  const offlineRooms = (rs.maintenance || 0) + (rs.out_of_service || 0);
  const totalSub = offlineRooms > 0
    ? `${rs.total_beds ?? 0} beds · ${offlineRooms} offline`
    : `${rs.total_beds ?? 0} beds total`;

  return (
    <div className="space-y-5">

      {/* ── Colorful gradient KPI tiles ────────────────────────────────────── */}
      <MetricCardGrid>
        <MetricCard
          variant="blue"
          label="Total Rooms"
          value={rs.total_rooms ?? "—"}
          sub={totalSub}
          icon={<Home className="w-5 h-5 text-white" />}
          data-testid="kpi-total-rooms"
        />
        <MetricCard
          variant="green"
          label="Available"
          value={rs.available ?? "—"}
          sub="rooms ready for booking"
          icon={<CheckCircle2 className="w-5 h-5 text-white" />}
          data-testid="kpi-available"
        />
        <MetricCard
          variant="amber"
          label="Occupied"
          value={rs.occupied ?? "—"}
          sub={`${bs.checked_in ?? 0} residents checked in`}
          icon={<Users className="w-5 h-5 text-white" />}
          data-testid="kpi-occupied"
        />
        <MetricCard
          variant="purple"
          label="Room Occupancy"
          value={`${roomOccPct}%`}
          sub={`of ${eligibleRooms} eligible rooms`}
          icon={<TrendingUp className="w-5 h-5 text-white" />}
          occupancyPct={roomOccPct}
          data-testid="kpi-occupancy-rate"
        />
      </MetricCardGrid>

      {/* ── Operational panels (light background) ──────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Bookings Summary */}
        <DashboardPanel
          title="Bookings Overview"
          icon={<CalendarCheck className="w-4 h-4 text-amber-500" />}
          data-testid="booking-stats-card"
        >
          <BookingsSummaryPanel
            checkedIn={bs.checked_in ?? 0}
            confirmed={bs.confirmed ?? 0}
            reserved={bs.reserved ?? 0}
            total={bs.total ?? 0}
          />
        </DashboardPanel>

        {/* Quick Access — spans remaining 2 columns */}
        <div className="md:col-span-2">
          <DashboardPanel
            title="Quick Access"
            icon={<ChevronRight className="w-4 h-4 text-slate-400" />}
            data-testid="quick-nav-card"
            className="h-full"
          >
            <QuickActionGrid
              actions={[
                {
                  label: "Room Inventory",
                  icon: <BedDouble className="w-4 h-4 text-blue-600" />,
                  iconBg: "bg-blue-50",
                  testid: "qnav-rooms",
                  onClick: () => navigate("/campus/hostel/rooms"),
                },
                {
                  label: "Rate Master",
                  icon: <Star className="w-4 h-4 text-amber-600" />,
                  iconBg: "bg-amber-50",
                  testid: "qnav-rates",
                  onClick: () => navigate("/campus/hostel/rates"),
                },
                {
                  label: "All Bookings",
                  icon: <CalendarCheck className="w-4 h-4 text-emerald-600" />,
                  iconBg: "bg-emerald-50",
                  testid: "qnav-bookings",
                  onClick: () => navigate("/campus/hostel/bookings"),
                },
                {
                  label: "GL Journals",
                  icon: <BookOpen className="w-4 h-4 text-purple-600" />,
                  iconBg: "bg-purple-50",
                  testid: "qnav-gl-journals",
                  onClick: () => navigate("/campus/hostel/gl/journals"),
                },
              ]}
            />
          </DashboardPanel>
        </div>
      </div>

      {/* ── Recent Bookings ──────────────────────────────────────────────────── */}
      <DashboardPanel
        title="Recent Bookings"
        icon={<Clock className="w-4 h-4 text-amber-500" />}
        action={
          <button
            className="text-xs font-semibold text-[#1765d1] hover:text-blue-700 transition-colors"
            onClick={() => navigate("/campus/hostel/bookings")}
            data-testid="btn-view-all-bookings"
          >
            View All →
          </button>
        }
        data-testid="recent-bookings-card"
      >
        <RecentBookingsList
          bookings={bookings}
          statusConfig={LIGHT_STATUS_CONFIG}
          onNavigate={navigate}
        />
      </DashboardPanel>

    </div>
  );
}

// ── GL Reconciliation Tab ─────────────────────────────────────────────────────
const GL_STATUS_CONFIG = {
  NO_JOURNAL:        { label: "No Journal",  color: "bg-red-500/20 text-red-300 border-red-500/30" },
  JOURNAL_DRAFT:     { label: "Draft",       color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  JOURNAL_VALIDATED: { label: "Validated",   color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  JOURNAL_APPROVED:  { label: "Approved",    color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  POSTED:            { label: "Posted",      color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
};

function GLReconciliationTab({ reconData, reconLoading, reconError, onRefresh, onInitiateGL, initiatingGL, canGLValidate, navigate }) {
  const [glFilter, setGlFilter] = useState("ALL");

  if (reconLoading) return (
    <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center"
      data-testid="recon-loading">
      <RefreshCw className="w-4 h-4 animate-spin" /> Loading reconciliation data…
    </div>
  );

  if (reconError) return (
    <div className="text-red-400 text-sm p-4 bg-red-900/10 border border-red-500/20 rounded-xl"
      data-testid="recon-error">
      <AlertCircle className="w-4 h-4 inline mr-2" />{reconError}
    </div>
  );

  const items = reconData?.items || [];
  const filtered = glFilter === "ALL" ? items
    : glFilter === "REQUIRES_ACTION" ? items.filter((i) => i.requires_action)
    : items.filter((i) => i.gl_status === glFilter);

  return (
    <div className="space-y-4" data-testid="gl-reconciliation-tab">
      {/* Stats row */}
      {reconData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Invoiced",  value: reconData.total_issued_invoices || 0, color: "text-slate-200" },
            { label: "GL Posted",       value: reconData.gl_posted || 0,              color: "text-emerald-400" },
            { label: "Needs Action",    value: reconData.requires_action || 0,        color: "text-red-400" },
            { label: "Historical",      value: items.filter((i) => i.fin01f_eligible).length, color: "text-amber-400" },
          ].map((s) => (
            <div key={s.label} className="bg-slate-800/60 border border-slate-700/40 rounded-xl p-3"
              data-testid={`recon-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-xs text-slate-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap text-xs">
          {["ALL", "NO_JOURNAL", "JOURNAL_DRAFT", "POSTED", "REQUIRES_ACTION"].map((f) => (
            <button key={f}
              data-testid={`recon-filter-${f.toLowerCase()}`}
              onClick={() => setGlFilter(f)}
              className={`px-3 py-1.5 rounded-lg border transition-all
                ${glFilter === f
                  ? "bg-amber-600/80 text-white border-amber-500/50"
                  : "bg-slate-800/60 text-slate-400 border-slate-700/40 hover:border-slate-600/60"
                }`}>
              {f.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs"
          onClick={onRefresh} data-testid="btn-refresh-recon">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Reconciliation table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm" data-testid="recon-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500/40" />
          No reconciliation items match the current filter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="recon-items-list">
          {filtered.map((item) => {
            const glSc = GL_STATUS_CONFIG[item.gl_status] || GL_STATUS_CONFIG.NO_JOURNAL;
            const isHistorical = item.fin01f_eligible;
            const showInitiate = canGLValidate && isHistorical;
            const isInitiating = initiatingGL === item.booking_id;

            return (
              <div key={item.booking_id}
                className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap"
                data-testid={`recon-item-${item.booking_id}`}>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-slate-700/50">
                    <CalendarCheck className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-mono text-white font-medium">
                        {item.booking_reference}
                      </span>
                      <Badge className={`text-xs border ${glSc.color}`}>{glSc.label}</Badge>
                      {isHistorical && (
                        <Badge className="text-xs border bg-amber-500/10 text-amber-300 border-amber-500/20">
                          Historical · FIN-01F Eligible
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Invoice: {item.invoice_number || "—"}
                      {item.invoice_total && ` · ₹${parseFloat(item.invoice_total).toLocaleString("en-IN")}`}
                    </p>
                    {item.journal_reference && (
                      <p className="text-xs text-slate-500">
                        Journal: <span className="font-mono">{item.journal_reference}</span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm"
                    className="text-slate-400 hover:text-white text-xs"
                    onClick={() => navigate(`/campus/hostel/bookings/${item.booking_id}`)}
                    data-testid={`btn-view-booking-${item.booking_id}`}>
                    View Detail
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                  {item.journal_id && (
                    <Button variant="ghost" size="sm"
                      className="text-amber-400 hover:text-amber-300 text-xs"
                      onClick={() => navigate(`/campus/hostel/gl/journals?ref=${item.journal_reference}`)}
                      data-testid={`btn-view-journal-${item.booking_id}`}>
                      <BookOpen className="w-3 h-3 mr-1" /> View Journal
                    </Button>
                  )}
                  {showInitiate && (
                    <Button size="sm"
                      className="bg-amber-700 hover:bg-amber-600 text-white text-xs"
                      onClick={() => onInitiateGL(item.booking_id)}
                      disabled={isInitiating}
                      data-testid={`btn-initiate-gl-${item.booking_id}`}>
                      <RotateCcw className={`w-3 h-3 mr-1 ${isInitiating ? "animate-spin" : ""}`} />
                      {isInitiating ? "Initiating…" : "Initiate GL"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* FIN-01F note */}
      {items.some((i) => i.fin01f_eligible) && (
        <div className="text-xs text-slate-500 border border-slate-700/30 rounded-lg p-3"
          data-testid="fin01f-note">
          <AlertCircle className="w-3 h-3 inline mr-1 text-amber-400" />
          Historical invoices marked <strong className="text-amber-400">FIN-01F Eligible</strong> have
          no GL journal. Finance Managers may initiate per-booking DRAFT journals.
          No auto-bulk processing. Each requires manual approval and posting via the GL Journals workspace.
        </div>
      )}
    </div>
  );
}

